import React, { useEffect, useMemo, useState } from "react";
import {
  FaEnvelope,
  FaFacebook,
  FaGlobe,
  FaInstagram,
  FaMapMarkerAlt,
  FaPhone,
  FaRoute,
  FaSms,
  FaUserTie,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";
import SectionTitle from "./../HomePage/SectionTitle";

import { getAllSections } from "../../../services/sectionsServices";

const cleanPhone = (phone = "") => String(phone).replace(/[^\d+]/g, "");

const SectionContacts = ({ sectionId = 1 }) => {
  const [section, setSection] = useState(null);
  const [mapUrl, setMapUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchSection = async () => {
      try {
        setLoading(true);

        const sections = await getAllSections();

        const currentSection = Array.isArray(sections)
          ? sections.find((sec) => String(sec.id) === String(sectionId))
          : null;

        if (mounted) setSection(currentSection || null);
      } catch (error) {
        console.error("Erreur lors de la récupération de la section:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchSection();

    return () => {
      mounted = false;
    };
  }, [sectionId]);

  useEffect(() => {
    if (section?.adresse_section) {
      const encodedAddress = encodeURIComponent(section.adresse_section);
      setMapUrl(`https://www.google.com/maps?q=${encodedAddress}&output=embed`);
    }
  }, [section]);

  const mapsLink = useMemo(() => {
    if (!section?.adresse_section) return null;

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      section.adresse_section
    )}`;
  }, [section]);

  if (loading) {
    return (
      <section className="bg-slate-50 py-16 dark:bg-slate-950">
        <div className="mx-auto max-w-7xl px-4 text-center text-slate-500 dark:text-slate-300">
          Chargement des coordonnées...
        </div>
      </section>
    );
  }

  if (!section) return null;

  const phone = cleanPhone(section.tel_section);

  return (
    <section className="bg-slate-50 py-16 dark:bg-slate-950 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow="Coordonnées"
          title="Contacts "
          highlight="& accès"
          subtitle="Retrouvez l’adresse, les moyens de contact et les liens utiles du service ou de la structure référente."
        />

        <Reveal>
          <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl dark:border-white/10 dark:bg-slate-900">
            <div className="grid lg:grid-cols-[1fr_0.95fr]">
              <div className="min-h-[320px] bg-slate-100 dark:bg-slate-950">
                {mapUrl ? (
                  <iframe
                    title={`Carte ${section.nom_section}`}
                    src={mapUrl}
                    className="h-full min-h-[320px] w-full"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : (
                  <div className="flex h-full min-h-[320px] items-center justify-center p-8 text-center text-slate-500 dark:text-slate-300">
                    Aucune adresse renseignée.
                  </div>
                )}
              </div>

              <div className="p-6 sm:p-8">
                <p className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary">
                  Service référent
                </p>

                <h3 className="mt-4 text-3xl font-bold text-slate-950 dark:text-white">
                  {section.nom_section}
                </h3>

                <div className="mt-6 grid gap-3">
                  <ContactLine
                    icon={FaMapMarkerAlt}
                    label="Adresse"
                    value={section.adresse_section}
                  />

                  <ContactLine
                    icon={FaUserTie}
                    label="Responsable"
                    value={section.responsable_section}
                  />

                  <ContactLine
                    icon={FaPhone}
                    label="Téléphone"
                    value={section.tel_section}
                    href={phone ? `tel:${phone}` : null}
                  />

                  <ContactLine
                    icon={FaEnvelope}
                    label="Email"
                    value={section.mail_section}
                    href={
                      section.mail_section
                        ? `mailto:${section.mail_section}`
                        : null
                    }
                  />

                  <ContactLine
                    icon={FaGlobe}
                    label="Site web"
                    value={section.siteweb_section}
                    href={section.siteweb_section}
                    external
                  />
                </div>

                <div className="mt-7 flex flex-wrap gap-3">
                  {phone && (
                    <a
                      href={`tel:${phone}`}
                      className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-3 text-sm font-bold text-white shadow transition hover:-translate-y-0.5 hover:shadow-lg"
                    >
                      <FaPhone />
                      Appeler
                    </a>
                  )}

                  {phone && (
                    <a
                      href={`sms:${phone}`}
                      className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow transition hover:-translate-y-0.5 hover:shadow-lg dark:bg-white dark:text-slate-950"
                    >
                      <FaSms />
                      SMS
                    </a>
                  )}

                  {section.mail_section && (
                    <a
                      href={`mailto:${section.mail_section}`}
                      className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white shadow transition hover:-translate-y-0.5 hover:shadow-lg"
                    >
                      <FaEnvelope />
                      Email
                    </a>
                  )}

                  {mapsLink && (
                    <a
                      href={mapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:text-primary dark:border-white/10 dark:bg-slate-950 dark:text-white"
                    >
                      <FaRoute />
                      Itinéraire
                    </a>
                  )}
                </div>

                {(section.facebook_section || section.instagram_section) && (
                  <div className="mt-7 flex flex-wrap gap-3 border-t border-slate-100 pt-6 dark:border-white/10">
                    {section.facebook_section && (
                      <SocialLink
                        href={section.facebook_section}
                        icon={FaFacebook}
                        label="Facebook"
                      />
                    )}

                    {section.instagram_section && (
                      <SocialLink
                        href={section.instagram_section}
                        icon={FaInstagram}
                        label="Instagram"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

function ContactLine({ icon: Icon, label, value, href, external = false }) {
  if (!value) return null;

  return (
    <div className="flex gap-3 rounded-2xl bg-slate-50 p-4 dark:bg-slate-950">
      <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Icon />
      </span>

      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
          {label}
        </p>

        {href ? (
          <a
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            className="mt-1 block break-words text-sm font-bold text-slate-700 transition hover:text-primary dark:text-slate-200"
          >
            {value}
          </a>
        ) : (
          <p className="mt-1 break-words text-sm font-bold text-slate-700 dark:text-slate-200">
            {value}
          </p>
        )}
      </div>
    </div>
  );
}

function SocialLink({ href, icon: Icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:text-primary hover:shadow-lg dark:border-white/10 dark:bg-slate-950 dark:text-white"
    >
      <Icon />
      {label}
    </a>
  );
}

export default SectionContacts;